import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AuthShell } from "./Login";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell heading="Forgot password" sub="Enter your email — we'll send you a secure link to set a new password.">
      {sent ? (
        <div className="mt-10 border border-gold-500/25 bg-ink-paper/40 p-8 text-center" data-testid="forgot-sent-panel">
          <CheckCircle2 size={40} className="text-gold-500 mx-auto mb-4" strokeWidth={1.5} />
          <p className="font-serif text-2xl text-white">Check your inbox</p>
          <p className="mt-3 text-sm text-white/60 leading-relaxed">
            If an account exists for <span className="text-gold-300">{email}</span>, a reset link is on its way. The link expires in 60 minutes.
          </p>
          <Link to="/login" data-testid="back-to-login" className="mt-6 inline-block text-xs uppercase tracking-[0.2em] text-gold-400 hover:text-gold-300">
            Back to sign in
          </Link>
        </div>
      ) : (
        <>
          <form onSubmit={submit} className="mt-10 space-y-4" data-testid="forgot-form">
            <Input
              required
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="forgot-email"
              className="bg-ink-paper border-gold-500/25 text-white placeholder:text-white/40 py-6"
            />
            <Button
              type="submit"
              disabled={busy}
              data-testid="forgot-submit"
              className="w-full bg-gold-500 text-ink hover:bg-gold-400 font-semibold py-6"
            >
              {busy ? "Sending…" : "Send reset link"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-white/60">
            Remembered it? <Link to="/login" className="text-gold-400 hover:underline" data-testid="to-login">Sign in</Link>
          </p>
        </>
      )}
    </AuthShell>
  );
}
